import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  TextField,
  IconButton,
  CircularProgress,
  Typography,
  Chip,
  Box,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Grid,
  Divider,
  Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { toast } from 'react-toastify';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';

const PRESET_TITLES = [
  'Merit Scholarship',
  'Sibling Concession',
  'Staff Ward Discount',
  'Financial Aid / EWS',
  'Sports Scholarship',
  'Management Concession',
  'Early Bird Rebate',
  'Custom Concession'
];

export default function BulkFeeConcessionModal({
  open,
  onClose,
  students = [],
  initialSelectedStudentIds = [],
  baseFeeStructure,
  studentFeeProfiles = {},
  selectedClassId,
  academicYear,
  onSaveSuccess
}) {
  const { axios: authAxios, token, user } = useAuth();
  const orgId = user?.orgId || '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';

  const [saving, setSaving] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());
  const [studentSearch, setStudentSearch] = useState('');
  const [termOverrides, setTermOverrides] = useState({});

  // Initialize selected students and term overrides only when modal is opened
  useEffect(() => {
    if (!open) return;

    // Pre-select students
    if (initialSelectedStudentIds && initialSelectedStudentIds.length > 0) {
      setSelectedStudentIds(new Set(initialSelectedStudentIds.map(String)));
    } else {
      // Default to selecting all students if none specified
      setSelectedStudentIds(new Set(students.map(s => String(s.userId || s.id || ''))));
    }

    // Initialize term overrides
    if (baseFeeStructure) {
      const installments = baseFeeStructure.feeInstallments || baseFeeStructure.installments || [];
      const initialMap = {};
      installments.forEach(inst => {
        const instId = inst.installmentId || inst.id;
        if (instId) {
          initialMap[instId] = {
            title: 'Merit Scholarship',
            discountType: 'FLAT',
            value: 0,
            remarks: ''
          };
        }
      });
      setTermOverrides(initialMap);
    }
  }, [open]);

  // Filtered student list in modal
  const filteredStudents = useMemo(() => {
    if (!studentSearch.trim()) return students;
    const q = studentSearch.toLowerCase();
    return students.filter(s => {
      const sId = String(s.userId || s.id || '').toLowerCase();
      const sName = `${s.firstName || ''} ${s.lastName || ''} ${s.displayName || ''}`.toLowerCase();
      return sName.includes(q) || sId.includes(q);
    });
  }, [students, studentSearch]);

  const allFilteredSelected = useMemo(() => {
    if (filteredStudents.length === 0) return false;
    return filteredStudents.every(s => selectedStudentIds.has(String(s.userId || s.id || '')));
  }, [filteredStudents, selectedStudentIds]);

  const handleToggleSelectAll = () => {
    setSelectedStudentIds(prev => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredStudents.forEach(s => next.delete(String(s.userId || s.id || '')));
      } else {
        filteredStudents.forEach(s => next.add(String(s.userId || s.id || '')));
      }
      return next;
    });
  };

  const handleToggleStudent = (sId) => {
    setSelectedStudentIds(prev => {
      const next = new Set(prev);
      const strId = String(sId);
      if (next.has(strId)) {
        next.delete(strId);
      } else {
        next.add(strId);
      }
      return next;
    });
  };

  // Calculate totals per student and aggregate
  const calculationSummary = useMemo(() => {
    if (!baseFeeStructure) return { baseTotal: 0, totalDiscount: 0, netTotal: 0, termCalculations: [] };

    const installments = baseFeeStructure.feeInstallments || baseFeeStructure.installments || [];
    let baseTotal = 0;
    let totalDiscount = 0;

    const termCalculations = installments.map(inst => {
      const instId = inst.installmentId || inst.id;
      const baseFee = Number(inst.totalFee || inst.amount || 0);
      baseTotal += baseFee;

      const override = termOverrides[instId] || { title: '', discountType: 'FLAT', value: 0, remarks: '' };
      let discountAmount = 0;
      const val = Number(override.value) || 0;

      if (override.discountType === 'PERCENTAGE') {
        discountAmount = (baseFee * Math.min(100, Math.max(0, val))) / 100;
      } else {
        discountAmount = Math.min(baseFee, Math.max(0, val));
      }

      totalDiscount += discountAmount;
      const netPayable = Math.max(0, baseFee - discountAmount);

      return {
        installmentId: instId,
        description: inst.description,
        baseFee,
        discountAmount,
        netPayable,
        override
      };
    });

    const netTotal = Math.max(0, baseTotal - totalDiscount);
    const countSelected = selectedStudentIds.size;
    const aggregateDiscount = totalDiscount * countSelected;

    return {
      baseTotal,
      totalDiscount,
      netTotal,
      termCalculations,
      countSelected,
      aggregateDiscount
    };
  }, [baseFeeStructure, termOverrides, selectedStudentIds]);

  const handleOverrideChange = (instId, field, val) => {
    setTermOverrides(prev => ({
      ...prev,
      [instId]: {
        ...prev[instId],
        [field]: val
      }
    }));
  };

  const handleSaveBulk = async () => {
    if (selectedStudentIds.size === 0) {
      toast.error('Please select at least one student to apply concession.');
      return;
    }

    if (!baseFeeStructure) {
      toast.error('No base fee structure loaded.');
      return;
    }

    try {
      setSaving(true);
      const selectedIdsArray = Array.from(selectedStudentIds);
      const installments = baseFeeStructure.feeInstallments || baseFeeStructure.installments || [];

      const overridesPayload = calculationSummary.termCalculations.map(tc => {
        const inst = installments.find(i => (i.installmentId || i.id) === tc.installmentId);
        const baseBreakdown = inst?.feeBreakdown || {};

        return {
          baseInstallmentId: tc.installmentId,
          customFeeBreakdown: baseBreakdown,
          netPayableFee: tc.netPayable,
          discounts: tc.override.value > 0 ? [
            {
              title: tc.override.title || 'Concession',
              discountType: tc.override.discountType,
              value: Number(tc.override.value),
              amount: tc.discountAmount,
              remarks: tc.override.remarks || ''
            }
          ] : []
        };
      });

      let successCount = 0;
      let errorCount = 0;

      // Save concession for each selected student
      const promises = selectedIdsArray.map(async (sId) => {
        const student = students.find(s => String(s.userId || s.id || '') === String(sId));
        const existingProfile = studentFeeProfiles[String(sId)];

        const payload = {
          studentId: sId,
          feeStructureId: baseFeeStructure.feeStructureId || baseFeeStructure.id,
          classId: selectedClassId || baseFeeStructure.classId,
          academicYear: academicYear || '2025-2026',
          orgId,
          netTotalFee: calculationSummary.netTotal,
          studentFeeOverrides: overridesPayload
        };

        try {
          if (existingProfile && existingProfile.studentFeeProfileId) {
            await authAxios.put(
              `${configData.STUDENT_FEE_PROFILE}/${existingProfile.studentFeeProfileId}`,
              payload,
              { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
          } else {
            await authAxios.post(
              configData.STUDENT_FEE_PROFILE,
              payload,
              { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
          }
          successCount++;
        } catch (err) {
          console.error(`Failed to apply concession for student ${sId}:`, err);
          errorCount++;
        }
      });

      await Promise.all(promises);

      if (successCount > 0) {
        toast.success(`Successfully applied fee concession to ${successCount} student(s)${errorCount > 0 ? ` (${errorCount} failed)` : ''}!`);
        if (onSaveSuccess) onSaveSuccess();
        onClose();
      } else {
        toast.error('Failed to apply fee concession for selected students.');
      }
    } catch (err) {
      console.error('Failed to execute bulk concession update:', err);
      toast.error('An error occurred while applying bulk concessions.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth className="fcm-dialog">
      <DialogTitle className="fcm-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <LocalOfferIcon />
            <h2 className="fcm-title">Apply Concession to Students</h2>
          </div>
        </div>
        <IconButton onClick={onClose} style={{ color: '#ffffff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ padding: '24px', backgroundColor: '#f8fafc' }}>
        {/* Aggregated Summary Banner */}
        <Box
          style={{
            background: '#ffffff',
            borderRadius: '14px',
            padding: '16px 20px',
            border: '1px solid #e2e8f0',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                background: '#eff6ff',
                color: '#014486',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <PeopleIcon />
            </div>
            <div>
              <Typography variant="h6" style={{ fontWeight: 700, fontSize: '16px', color: '#0f172a' }}>
                {calculationSummary.countSelected} Student(s) Selected
              </Typography>
              <Typography variant="body2" style={{ color: '#64748b', fontSize: '13px' }}>
                Out of {students.length} students in this class
              </Typography>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Base Fee / Student</span>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#334155' }}>
                ₹{calculationSummary.baseTotal.toLocaleString('en-IN')}
              </div>
            </div>

            <div style={{ textAlign: 'right' }}>
              <span style={{ fontSize: '11px', color: '#047857', textTransform: 'uppercase', fontWeight: 600 }}>Concession / Student</span>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#047857' }}>
                - ₹{calculationSummary.totalDiscount.toLocaleString('en-IN')}
              </div>
            </div>

            <div style={{ textAlign: 'right', background: '#ecfdf5', padding: '8px 14px', borderRadius: '10px', border: '1px solid #a7f3d0' }}>
              <span style={{ fontSize: '11px', color: '#047857', textTransform: 'uppercase', fontWeight: 700 }}>Total Class Concessions</span>
              <div style={{ fontSize: '17px', fontWeight: 800, color: '#047857' }}>
                - ₹{calculationSummary.aggregateDiscount.toLocaleString('en-IN')}
              </div>
            </div>
          </div>
        </Box>

        <Grid container spacing={3}>
          {/* Left Panel: Student Selection */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              style={{
                borderRadius: '14px',
                border: '1px solid #e2e8f0',
                padding: '16px',
                background: '#ffffff',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <Typography style={{ fontWeight: 700, fontSize: '14px', color: '#1e293b' }}>
                  Select Students
                </Typography>
                <Chip
                  label={`${selectedStudentIds.size}/${students.length}`}
                  size="small"
                  color="primary"
                  style={{ fontWeight: 700, backgroundColor: '#014486' }}
                />
              </div>

              {/* Student Search */}
              <TextField
                size="small"
                fullWidth
                placeholder="Search student name..."
                value={studentSearch}
                onChange={e => setStudentSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ fontSize: '18px', color: '#94a3b8' }} />
                    </InputAdornment>
                  )
                }}
                style={{ marginBottom: '12px' }}
              />

              {/* Select All Checkbox & Clear All */}
              <div
                style={{
                  padding: '8px 12px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #f1f5f9',
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={allFilteredSelected}
                      onChange={handleToggleSelectAll}
                      style={{ color: '#014486' }}
                    />
                  }
                  label={
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                      Select All Filtered ({filteredStudents.length})
                    </span>
                  }
                  style={{ margin: 0 }}
                />
                <Button
                  size="small"
                  onClick={() => setSelectedStudentIds(new Set())}
                  style={{ fontSize: '11px', textTransform: 'none', color: '#64748b', minWidth: 'auto', padding: '2px 6px' }}
                >
                  Clear All
                </Button>
              </div>

              {/* Student List with Scroll */}
              <div style={{ flex: 1, maxHeight: '340px', overflowY: 'auto', paddingRight: '4px' }}>
                {filteredStudents.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px 12px', color: '#94a3b8', fontSize: '13px' }}>
                    No students match search
                  </div>
                ) : (
                  filteredStudents.map(student => {
                    const sId = String(student.userId || student.id || '');
                    const isChecked = selectedStudentIds.has(sId);
                    const sName = student.displayName || `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Student';
                    const profile = studentFeeProfiles[sId];
                    const hasExistingConcession = profile && profile.studentFeeOverrides && profile.studentFeeOverrides.some(ov => ov.discounts && ov.discounts.length > 0);

                    return (
                      <div
                        key={sId}
                        onClick={() => handleToggleStudent(sId)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          marginBottom: '6px',
                          cursor: 'pointer',
                          background: isChecked ? '#eff6ff' : '#ffffff',
                          border: isChecked ? '1px solid #93c5fd' : '1px solid #e2e8f0',
                          boxShadow: isChecked ? '0 1px 3px rgba(1, 68, 134, 0.08)' : 'none',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={isChecked}
                          style={{ color: '#014486', padding: '2px', marginRight: '8px', pointerEvents: 'none' }}
                        />
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: isChecked ? '#014486' : '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {sName}
                          </div>
                          {hasExistingConcession ? (
                            <span style={{ fontSize: '11px', color: '#047857', fontWeight: 500 }}>
                              • Existing concession
                            </span>
                          ) : (
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                              Standard fee
                            </span>
                          )}
                        </div>
                        {isChecked && <CheckCircleIcon style={{ fontSize: '18px', color: '#014486' }} />}
                      </div>
                    );
                  })
                )}
              </div>
            </Paper>
          </Grid>

          {/* Right Panel: Concession Breakdown Form */}
          <Grid item xs={12} md={8}>
            <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#1e293b', marginBottom: '12px' }}>
              Concession Rules (Applied to selected students)
            </Typography>

            <div style={{ maxHeight: '440px', overflowY: 'auto', paddingRight: '4px' }}>
              {calculationSummary.termCalculations.map((tc) => {
                const ov = tc.override;
                const hasDiscount = tc.discountAmount > 0;

                return (
                  <div key={tc.installmentId} className={`fcm-term-card ${hasDiscount ? 'has-discount' : ''}`}>
                    <div className="fcm-term-header">
                      <div>
                        <span className="fcm-term-title">{tc.description}</span>
                        <span className="fcm-term-base" style={{ marginLeft: '12px' }}>
                          Base Term Fee: ₹{tc.baseFee.toLocaleString('en-IN')}
                        </span>
                      </div>
                      {hasDiscount && (
                        <Chip
                          label={`Discount: ₹${tc.discountAmount.toLocaleString('en-IN')} (${tc.netPayable === 0 ? 'Full Waiver' : `Net: ₹${tc.netPayable.toLocaleString('en-IN')}`})`}
                          size="small"
                          style={{ background: '#dcfce7', color: '#15803d', fontWeight: 700 }}
                        />
                      )}
                    </div>

                    <div className="fcm-term-inputs">
                      {/* Discount Category / Title */}
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>
                          Concession Title
                        </label>
                        <Select
                          size="small"
                          fullWidth
                          value={ov.title || 'Merit Scholarship'}
                          onChange={(e) => handleOverrideChange(tc.installmentId, 'title', e.target.value)}
                        >
                          {PRESET_TITLES.map(title => (
                            <MenuItem key={title} value={title}>{title}</MenuItem>
                          ))}
                        </Select>
                      </div>

                      {/* Discount Type */}
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>
                          Discount Type
                        </label>
                        <Select
                          size="small"
                          fullWidth
                          value={ov.discountType || 'FLAT'}
                          onChange={(e) => handleOverrideChange(tc.installmentId, 'discountType', e.target.value)}
                        >
                          <MenuItem value="FLAT">Flat (₹)</MenuItem>
                          <MenuItem value="PERCENTAGE">Percentage (%)</MenuItem>
                        </Select>
                      </div>

                      {/* Discount Value */}
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>
                          {ov.discountType === 'PERCENTAGE' ? 'Discount (%)' : 'Discount Amount (₹)'}
                        </label>
                        <TextField
                          size="small"
                          type="number"
                          fullWidth
                          placeholder="0"
                          value={ov.value || ''}
                          onChange={(e) => handleOverrideChange(tc.installmentId, 'value', e.target.value)}
                          inputProps={{ min: 0, max: ov.discountType === 'PERCENTAGE' ? 100 : tc.baseFee }}
                        />
                      </div>

                      {/* Net Term Result */}
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>
                          Net Term Fee
                        </label>
                        <div
                          style={{
                            height: '40px',
                            borderRadius: '4px',
                            background: '#f1f5f9',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 12px',
                            fontWeight: 700,
                            color: tc.netPayable < tc.baseFee ? '#047857' : '#1e293b',
                            border: '1px solid #cbd5e1'
                          }}
                        >
                          ₹{tc.netPayable.toLocaleString('en-IN')}
                        </div>
                      </div>
                    </div>

                    {/* Remarks / Description */}
                    <div>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Remarks / Description (e.g. Granted by Principal for Merit)..."
                        value={ov.remarks || ''}
                        onChange={(e) => handleOverrideChange(tc.installmentId, 'remarks', e.target.value)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px', background: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
        <Button onClick={onClose} disabled={saving} style={{ color: '#64748b' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSaveBulk}
          disabled={saving || selectedStudentIds.size === 0}
          variant="contained"
          style={{
            background: selectedStudentIds.size > 0 ? '#014486' : '#cbd5e1',
            color: '#ffffff',
            fontWeight: 700,
            padding: '8px 24px',
            borderRadius: '10px'
          }}
        >
          {saving ? (
            <CircularProgress size={22} color="inherit" />
          ) : (
            `Apply Concession to ${selectedStudentIds.size} Student(s)`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
